import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  message,
  Empty,
  Popconfirm,
  Tooltip,
  Spin,
} from "antd";
import { PlusOutlined, DeleteOutlined, FlagOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import customClientAxios from "../customAxios";
import * as Api from "../utils/ApiEndpoints";

const PageWrapper = styled.div`
  padding: 8px 4px 32px;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
const PageHeader = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  margin-bottom: 28px; gap: 16px; flex-wrap: wrap;
`;
const PageTitle = styled.h1`
  font-size: 28px; font-weight: 700; color: #0f172a;
  letter-spacing: -0.5px; margin: 0;
`;
const PageSubtitle = styled.p`
  font-size: 14px; color: #64748b; margin: 6px 0 0; max-width: 720px;
`;

const GoalCard = styled.div`
  background: white; border-radius: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  padding: 24px 26px;
  margin-bottom: 20px;
`;
const GoalHead = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 18px;
`;
const GoalTitle = styled.div`
  font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 4px;
`;
const GoalMeta = styled.div`
  font-size: 13px; color: #64748b;
`;
const BigNumber = styled.div`
  font-size: 36px; font-weight: 800; color: #0f172a;
  letter-spacing: -1px; line-height: 1.1;
`;
const TargetText = styled.span`
  font-size: 16px; font-weight: 500; color: #94a3b8; margin-left: 8px;
`;
const ProgressBarOuter = styled.div`
  height: 14px; background: #f1f5f9; border-radius: 999px;
  overflow: hidden; margin: 14px 0 6px;
`;
const ProgressBarInner = styled.div`
  height: 100%; background: linear-gradient(90deg, #6366f1, #8b5cf6);
  width: ${(p) => p.$pct}%;
  transition: width 0.4s ease;
`;
const ProgressMeta = styled.div`
  display: flex; justify-content: space-between;
  font-size: 13px; color: #64748b; margin-bottom: 24px;
`;
const StatGrid = styled.div`
  display: grid; gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 6px;
`;
const StatBox = styled.div`
  background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 12px; padding: 14px 16px;
`;
const StatLabel = styled.div`
  font-size: 12px; color: #64748b; font-weight: 500;
  text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 6px;
`;
const StatValue = styled.div`
  font-size: 22px; font-weight: 700; color: #0f172a;
`;
const StatSub = styled.div`
  font-size: 12px; color: #475569; margin-top: 4px;
`;
const SplitRow = styled.div`
  display: flex; gap: 16px; margin-top: 16px;
`;
const SplitPill = styled.div`
  flex: 1; background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  border-radius: 10px; padding: 12px 14px;
`;
const SplitLabel = styled.div`
  font-size: 12px; color: #475569; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px;
`;
const SplitValue = styled.div`
  font-size: 18px; font-weight: 700; color: #0f172a;
`;
const SectionTitle = styled.div`
  font-size: 13px; font-weight: 600; color: #475569;
  text-transform: uppercase; letter-spacing: 0.5px;
  margin: 24px 0 10px;
`;

const formatINR = (paise) => {
  const rupees = (paise || 0) / 100;
  return rupees.toLocaleString("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  });
};
const formatCompactINR = (paise) => {
  const rupees = (paise || 0) / 100;
  if (rupees >= 10000000) return `₹${(rupees / 10000000).toFixed(2)} Cr`;
  if (rupees >= 100000) return `₹${(rupees / 100000).toFixed(2)} L`;
  if (rupees >= 1000) return `₹${(rupees / 1000).toFixed(1)}k`;
  return `₹${Math.round(rupees)}`;
};

export default function GoalsPage() {
  const [items, setItems] = useState([]); // [{ goal, progress }]
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const listRes = await customClientAxios().get(Api.AdminGoalsList);
      const goals = listRes.data?.data || [];
      const enriched = await Promise.all(
        goals.map(async (g) => {
          try {
            const pr = await customClientAxios().get(Api.AdminGoalProgress(g._id));
            return pr.data?.data || { goal: g, progress: null };
          } catch {
            return { goal: g, progress: null };
          }
        })
      );
      setItems(enriched);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load goals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitNew = async (values) => {
    setSubmitting(true);
    try {
      await customClientAxios().post(Api.AdminGoalsList, {
        title: values.title,
        description: values.description || "",
        metricType: "mrr_with_breakdown",
        targetAmount: Math.round(Number(values.targetRupees) * 100),
        perUserThreshold: Math.round(Number(values.thresholdRupees || 2000) * 100),
        endDate: values.endDate.endOf("day").toISOString(),
      });
      message.success("Goal created");
      setCreateOpen(false);
      form.resetFields();
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to create goal");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await customClientAxios().delete(Api.AdminGoalById(id));
      message.success("Goal removed");
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to remove goal");
    }
  };

  return (
    <PageWrapper>
      <PageHeader>
        <div>
          <PageTitle><FlagOutlined style={{ marginRight: 10, color: "#6366f1" }} />Founder Goals</PageTitle>
          <PageSubtitle>
            North-star targets the whole team rallies behind. Each goal tracks
            real MRR (active subscription revenue + this-month platform fee),
            with a split so we can see what's driving the number.
          </PageSubtitle>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          New Goal
        </Button>
      </PageHeader>

      {loading ? (
        <div style={{ textAlign: "center", padding: 80 }}><Spin /></div>
      ) : items.length === 0 ? (
        <GoalCard>
          <Empty description="No goals yet. Create your first goal to align the team." />
        </GoalCard>
      ) : (
        items.map(({ goal, progress }) => (
          <GoalCard key={goal._id}>
            <GoalHead>
              <div>
                <GoalTitle>{goal.title}</GoalTitle>
                {goal.description && (
                  <div style={{ fontSize: 14, color: "#475569", marginTop: 2 }}>
                    {goal.description}
                  </div>
                )}
                <GoalMeta style={{ marginTop: 6 }}>
                  Target: <b>{formatINR(goal.targetAmount)}</b>
                  {" · "}Deadline: <b>{dayjs(goal.endDate).format("MMM D, YYYY")}</b>
                  {goal.createdBy ? <>{" · "}By {goal.createdBy}</> : null}
                </GoalMeta>
              </div>
              <Popconfirm
                title="Remove this goal?"
                description="It will be hidden from the dashboard and banner."
                onConfirm={() => onDelete(goal._id)}
                okText="Remove" cancelText="Cancel"
              >
                <Button danger type="text" icon={<DeleteOutlined />} />
              </Popconfirm>
            </GoalHead>

            {progress ? (
              <>
                <BigNumber>
                  {formatINR(progress.totalRevenue)}
                  <TargetText>/ {formatINR(progress.targetAmount)}</TargetText>
                </BigNumber>
                <ProgressBarOuter>
                  <ProgressBarInner $pct={Math.min(100, progress.percent || 0)} />
                </ProgressBarOuter>
                <ProgressMeta>
                  <span>{(progress.percent || 0).toFixed(1)}% complete</span>
                  <span>
                    {progress.isOverdue
                      ? `Overdue by ${Math.abs(progress.daysLeft || 0)} days`
                      : `${progress.daysLeft} days left`}
                  </span>
                </ProgressMeta>

                <SectionTitle>Revenue composition (calendar month)</SectionTitle>
                <SplitRow>
                  <SplitPill $bg="#eef2ff" $border="#c7d2fe">
                    <SplitLabel>Subscription</SplitLabel>
                    <SplitValue>{formatINR(progress.subscriptionRevenue)}</SplitValue>
                    <StatSub>Active paid subs</StatSub>
                  </SplitPill>
                  <SplitPill $bg="#ecfdf5" $border="#a7f3d0">
                    <SplitLabel>Platform Fee</SplitLabel>
                    <SplitValue>{formatINR(progress.platformFeeRevenue)}</SplitValue>
                    <StatSub>Ticket transactions this month</StatSub>
                  </SplitPill>
                </SplitRow>

                <SectionTitle>Users</SectionTitle>
                <StatGrid>
                  <StatBox>
                    <StatLabel>Paying users</StatLabel>
                    <StatValue>{progress.payingUsersCount}</StatValue>
                    <StatSub>{progress.payingBrandPagesCount} brand pages on active paid plans</StatSub>
                  </StatBox>
                  <StatBox>
                    <StatLabel>Driving ≥ {formatCompactINR(goal.perUserThreshold)}/mo</StatLabel>
                    <StatValue>{progress.drivers?.total ?? 0}</StatValue>
                    <StatSub>
                      {progress.drivers?.subscribed ?? 0} subscribed
                      {" · "}
                      {progress.drivers?.notSubscribed ?? 0} not subscribed
                    </StatSub>
                  </StatBox>
                  <StatBox>
                    <Tooltip title="Sum of contributions (sub + platform fee) / paying users">
                      <StatLabel>Avg / paying user</StatLabel>
                    </Tooltip>
                    <StatValue>
                      {progress.payingUsersCount > 0
                        ? formatINR(Math.round(progress.totalRevenue / progress.payingUsersCount))
                        : "—"}
                    </StatValue>
                    <StatSub>This calendar month</StatSub>
                  </StatBox>
                </StatGrid>
              </>
            ) : (
              <div style={{ color: "#94a3b8", padding: "8px 0" }}>
                Progress unavailable
              </div>
            )}
          </GoalCard>
        ))
      )}

      <Modal
        title="Create a new goal"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={submitNew}
          initialValues={{
            thresholdRupees: 2000,
          }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Goal needs a title" }]}
          >
            <Input placeholder="e.g. 200 paying users driving ₹2k each by end of June" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} placeholder="Why this goal matters" />
          </Form.Item>
          <Form.Item
            name="targetRupees"
            label="Target MRR (₹)"
            rules={[{ required: true, message: "Set the rupee target" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              step={10000}
              placeholder="400000"
              formatter={(v) => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => v.replace(/[^\d]/g, "")}
            />
          </Form.Item>
          <Form.Item
            name="thresholdRupees"
            label="Per-user driver threshold (₹/month)"
            tooltip="A user contributing at least this much (sub + platform fee) is counted as a driver."
          >
            <InputNumber style={{ width: "100%" }} min={0} step={500} />
          </Form.Item>
          <Form.Item
            name="endDate"
            label="Deadline"
            rules={[{ required: true, message: "Pick a deadline" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrapper>
  );
}
